import React, { useState, useMemo } from 'react';
import { Check, FileText, Image, MessageSquare, Languages, Scissors } from 'lucide-react';
import FadeIn from './FadeIn';

const getPricePerLicense = (n) => {
  if (n <= 3) return 299;
  if (n <= 4) return Math.round(299 + (229 - 299) * ((n - 3) / (5 - 3)));
  if (n <= 9) return Math.round(229 + (199 - 229) * ((n - 5) / (10 - 5)));
  const anchors = [[10, 199], [20, 169], [50, 149], [100, 129]];
  for (let i = 0; i < anchors.length - 1; i++) {
    const [n1, p1] = anchors[i];
    const [n2, p2] = anchors[i + 1];
    if (n >= n1 && n <= n2) {
      const t = (Math.log(n) - Math.log(n1)) / (Math.log(n2) - Math.log(n1));
      return Math.round(p1 + (p2 - p1) * t);
    }
  }
  return 129;
};

const ACTION_COSTS = [
  { key: 'materials', label: 'příprav, testů a pracovních listů', cost: 0.2, icon: FileText },
  { key: 'images', label: 'obrázků a ilustrací do výuky', cost: 0.85, icon: Image },
  { key: 'chat', label: 'odpovědí od AI asistenta', cost: 0.15, icon: MessageSquare },
  { key: 'translate', label: 'překladů nebo detekcí AI textu', cost: 0.14, icon: Languages },
  { key: 'summarize', label: 'shrnutí videí nebo dokumentů', cost: 0.32, icon: Scissors },
];

const fmt = (n) => n.toLocaleString('cs-CZ');

const Pricing = () => {
  const [licenses, setLicenses] = useState(10);

  const calc = useMemo(() => {
    const ppl = getPricePerLicense(licenses);
    const monthly = ppl * licenses;
    const yearly = monthly * 12;
    const credits = Math.round(monthly * 3.7);
    const budget = monthly * (100 / 299);
    const capacities = ACTION_COSTS.map(a => ({ ...a, max: Math.ceil(Math.floor(budget / a.cost) / 10) * 10 }));
    return { ppl, monthly, yearly, credits, capacities };
  }, [licenses]);

  const sliderPercent = ((licenses - 3) / (100 - 3)) * 100;

  return (
    <section id="pricing" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[color:var(--text-primary)] mb-4">Ceník</h2>
            <p className="text-lg text-[color:var(--text-secondary)]">Transparentní ceny pro individuální pedagogy i celé školy</p>
          </div>
        </FadeIn>

        {/* ── School license with slider ── */}
        <FadeIn delay={0.1}>
          <div className="max-w-3xl mx-auto mb-10">
            <div className="relative bg-white rounded-3xl border-2 border-[color:var(--primary-500)] shadow-xl overflow-hidden">
              <div className="absolute -top-0.5 left-1/2 -translate-x-1/2 bg-[color:var(--primary-500)] text-white px-5 py-1.5 rounded-b-xl text-xs font-semibold">
                Nejoblíbenější
              </div>
              <div className="pt-10 pb-8 px-6 md:px-10">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-[color:var(--text-primary)] mb-1">Školní licence</h3>
                  <p className="text-sm text-[color:var(--text-secondary)]">Stejné funkce · výhodnější cena · jedna faktura pro celou školu</p>
                </div>

                <div className="max-w-xl mx-auto">
                  <div className="flex items-end justify-between mb-2">
                    <span className="text-sm font-semibold text-[color:var(--text-primary)]">Kolik pedagogů zapojíte?</span>
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold text-[color:var(--primary-600)] tabular-nums leading-none">{licenses}</span>
                      <span className="text-sm text-[color:var(--text-muted)]">{licenses < 5 ? 'licence' : 'licencí'}</span>
                    </div>
                  </div>

                  <div className="relative py-3">
                    <div className="h-2 bg-gray-200 rounded-full">
                      <div className="h-2 rounded-full transition-all duration-75" style={{ width: `${sliderPercent}%`, backgroundColor: '#008B8B' }} />
                    </div>
                    <input type="range" min="3" max="100" step="1" value={licenses} onChange={(e) => setLicenses(parseInt(e.target.value))} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
                    <div className="absolute top-1/2 -translate-y-1/2 w-6 h-6 bg-white border-[3px] border-[#008B8B] rounded-full shadow-lg pointer-events-none transition-all duration-75" style={{ left: `calc(${sliderPercent}% - 12px)` }} />
                  </div>
                  <div className="flex justify-between text-[10px] text-[color:var(--text-muted)] mt-1">
                    <span>3</span><span>25</span><span>50</span><span>75</span><span>100</span>
                  </div>

                  <div className="mt-6 text-center">
                    <div className="text-[10px] uppercase tracking-wider text-[color:var(--text-muted)] mb-1">Cena za pedagoga</div>
                    <div className="flex items-baseline justify-center gap-2">
                      <span className="text-4xl font-bold text-[color:var(--text-primary)] tabular-nums">{fmt(calc.ppl)}</span>
                      <span className="text-base text-[color:var(--text-muted)]">Kč / měsíc</span>
                    </div>
                    <div className="flex items-center justify-center gap-5 mt-2 text-sm text-[color:var(--text-secondary)]">
                      <span><strong className="text-[color:var(--text-primary)] tabular-nums">{fmt(calc.monthly)} Kč</strong> škola / měsíc</span>
                      <span className="w-px h-4 bg-gray-300" />
                      <span><strong className="text-[color:var(--text-primary)] tabular-nums">{fmt(calc.yearly)} Kč</strong> škola / rok</span>
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-gray-100">
                  <h4 className="text-sm font-bold text-[color:var(--text-primary)] mb-3">S tímto plánem vaše škola měsíčně zvládne například</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                    {calc.capacities.map((cap) => {
                      const Icon = cap.icon;
                      return (
                        <div key={cap.key} className="flex items-center gap-3 py-0.5">
                          <Icon className="w-4 h-4 text-[color:var(--primary-500)] flex-shrink-0" />
                          <span className="text-sm text-[color:var(--text-secondary)]">až <strong className="text-[color:var(--text-primary)] tabular-nums">{fmt(cap.max)}</strong> {cap.label}</span>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-[10px] text-[color:var(--text-muted)] mt-2">{fmt(calc.credits)} kreditů k dispozici · kapacity jsou orientační</p>
                </div>

                <a href="https://app.skolobot.cz/register" target="_blank" rel="noopener noreferrer" className="block mt-6">
                  <button className="w-full py-4 rounded-xl font-semibold bg-[color:var(--primary-500)] text-white hover:bg-[color:var(--primary-600)] shadow-md hover:shadow-lg transition-all text-lg">
                    Objednat licenci
                  </button>
                </a>
              </div>
            </div>
          </div>
        </FadeIn>

        {/* ── Individual ── */}
        <FadeIn delay={0.2}>
          <div className="max-w-xl mx-auto">
            <div className="bg-white rounded-2xl p-7 border-2 border-[color:var(--primary-100)] shadow-md">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold text-[color:var(--text-primary)]">Pro jednotlivce</h3>
                  <p className="text-xs text-[color:var(--text-secondary)]">Kompletní řešení pro aktivního pedagoga</p>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-[color:var(--text-primary)]">399 Kč</div>
                  <div className="text-xs text-[color:var(--text-muted)]">/ měsíc · 4 788 Kč / rok</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1.5 mb-4">
                {['Všechny šablony (100+)', 'AI chat', 'Obrázky', 'Přepis videí', 'Překlady 50+ jazyků', 'AI detektor', 'Export Word/PDF'].map((f, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    <Check className="w-3.5 h-3.5 text-[color:var(--primary-500)]" />
                    <span className="text-xs text-[color:var(--text-secondary)]">{f}</span>
                  </div>
                ))}
              </div>
              <a href="https://app.skolobot.cz/register" target="_blank" rel="noopener noreferrer">
                <button className="w-full py-3 rounded-xl font-semibold bg-[color:var(--primary-500)] text-white hover:bg-[color:var(--primary-600)] shadow-sm hover:shadow-md transition-all text-sm">
                  Objednat licenci
                </button>
              </a>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>
  );
};

export default Pricing;
